import { CommonModule } from "@angular/common";
import { Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { DialogModule } from "primeng/dialog";
import { TagModule } from "primeng/tag";
import { firstValueFrom, Subscription } from "rxjs";
import { CONSTANTS } from "../../../../../../constants";
import { db, InspectionJob, Lot } from "../../../../../../core/database/db";
import { HorizontalButton, HorizontalButtonGroupComponent } from "../../../../../../shared/components/horizontal-button-group/horizontal-button-group.component";
import { VerticalButton, VerticalButtonGroupComponent } from "../../../../../../shared/components/vertical-button-group/vertical-button-group.component";
import { StandardBottomSheetComponent } from "../../../../../../shared/components/standard-bottom-sheet/standard-bottom-sheet.component";
import { UiToastService } from "../../../../../../shared/components/toast/ui-toast.service";
import { ExtendedRouter } from "../../../../../../shared/services/extended-router.service";
import { NetworkStatusService } from "../../../../../../shared/services/network-status.service";
import { DateUtilsService } from "../../../../../../shared/services/date.service";
import { HomeOwnerService } from "../../../../../homeowner/services/homeowner-service";
import { FeatureFlag, FeatureService } from "../../../../../../core/feature-flags/feature.service";
import { ChecklistService } from "../../../../../../shared/services/checklist.service";
import JobDataService from "../../../../../../domains/jobs/services/job-data-service";

export enum ScoreCardCompleteActionType {
  CHECK = "check",
  LINK = "link"
}

export enum ScoreCardStatus {
  Active = "active",
  Pending = "pending",
  Complete = "complete"
}

export interface ScoreCardDefinition {
  stepName: string;
  getStartText: (job: InspectionJob) => string;
  getCompleteText: (job: InspectionJob) => string;
  completeActionType: ScoreCardCompleteActionType;
  completeActionLabel: string;
  canJobBePerformedFromSummary: boolean;
  canJobBeStarted: () => boolean;
  onStart: (job: InspectionJob) => void;
  onCompleteAction: (job: InspectionJob) => void;
  isFeatureEnabled: () => boolean;
  detailsTitle: string;
  hasInternalReport: boolean;
  canResendHomeownerEmail: boolean;
  /** Reason the CM cannot start the step yet, or null when it can be started early. */
  getBlockedReason: () => string | null;
}

export interface ScoreCard {
  job: InspectionJob;
  definition: ScoreCardDefinition;
  status: ScoreCardStatus;
  title: string;
  subtext: string;
}

export interface ScoreCardGroup {
  key: ScoreCardStatus;
  /** Empty for the active group, which the sticky "Build Quality Steps" header already labels. */
  label: string;
  items: ScoreCard[];
}

/** Sheet body copy split so the emphasised phrase can be bolded. */
export interface StepSheetMessage {
  leading: string;
  emphasis: string;
  trailing: string;
}

/** Steps scheduled this close (in business days) are actionable without starting early. */
const READY_WINDOW_BUSINESS_DAYS = 2;

/** Thin space, so the step number sits tighter to its name than a full space allows. */
const STEP_NUMBER_SEPARATOR = ".\u2009";

@Component({
  selector: "app-score-card-list",
  standalone: true,
  imports: [CommonModule, DialogModule, TagModule, HorizontalButtonGroupComponent, VerticalButtonGroupComponent, StandardBottomSheetComponent],
  templateUrl: "./score-card-list.component.html",
  styleUrl: "./score-card-list.component.scss"
})
export class ScoreCardListComponent implements OnInit, OnDestroy {
  @Input({ required: true }) jobs: InspectionJob[] = [];
  @Input() lot: Lot | undefined;
  /** When false, only the active steps are rendered. */
  @Input() showAllSteps = false;

  preClosingModalOpen = false;
  activeJob: InspectionJob | null = null;
  activeJobDefinition: ScoreCardDefinition | null = null;
  activeJobIssueCount: number | null = null;
  activeInternalReportUrl: string | null = null;
  sendingEmails = false;
  online = true;

  isStepSheetOpen = false;
  stepSheetCard: ScoreCard | null = null;

  ScoreCardCompleteActionType = ScoreCardCompleteActionType;
  ScoreCardStatus = ScoreCardStatus;

  private router = inject(ExtendedRouter);
  private homeownerService = inject(HomeOwnerService);
  private toastService = inject(UiToastService);
  private networkStatusService = inject(NetworkStatusService);
  private onlineSubscription: Subscription;
  private featureService = inject(FeatureService);
  private checklistService = inject(ChecklistService);
  private dateUtils = inject(DateUtilsService);
  private jobDataService = inject(JobDataService);
  private isBq234Enabled = false;
  private isBq15Enabled = false;
  private isBq6Enabled = false;
  private isBq9Enabled = false;
  private isBq8ChecklistAvailable = true;

  readonly definitions: Record<string, ScoreCardDefinition> = {
    "BQ1": this.createSingleAreaInspectionDefinition(
      CONSTANTS.BUILD_QUALITY_STEPS.BQ1,
      CONSTANTS.BUILD_QUALITY_STEP_TITLES.BQ1,
      () => this.isBq15Enabled,
      true
    ),
    "BQ2": this.createSingleAreaInspectionDefinition(
      CONSTANTS.BUILD_QUALITY_STEPS.BQ2,
      CONSTANTS.BUILD_QUALITY_STEP_TITLES.BQ2,
      () => this.isBq234Enabled
    ),
    "BQ3": this.createSingleAreaInspectionDefinition(
      CONSTANTS.BUILD_QUALITY_STEPS.BQ3,
      CONSTANTS.BUILD_QUALITY_STEP_TITLES.BQ3,
      () => this.isBq234Enabled
    ),
    "BQ4": this.createSingleAreaInspectionDefinition(
      CONSTANTS.BUILD_QUALITY_STEPS.BQ4,
      CONSTANTS.BUILD_QUALITY_STEP_TITLES.BQ4,
      () => this.isBq234Enabled
    ),
    "BQ5": this.createSingleAreaInspectionDefinition(
      CONSTANTS.BUILD_QUALITY_STEPS.BQ5,
      CONSTANTS.BUILD_QUALITY_STEP_TITLES.BQ5,
      () => this.isBq15Enabled,
      true
    ),
    "BQ6": {
      stepName: CONSTANTS.BUILD_QUALITY_STEP_TITLES.BQ6,
      getStartText: job => (job.startedAt ? "Continue BQ6" : "Start BQ6"),
      getCompleteText: _job => "BQ6 Complete",
      completeActionType: ScoreCardCompleteActionType.LINK,
      completeActionLabel: "View",
      canJobBeStarted: () => true,
      canJobBePerformedFromSummary: true,
      onStart: job => this.router.navigateWithPersistedState(["/cm/bq6/job", job.id], { state: { fromLotSummary: true } }),
      onCompleteAction: job => this.openModal(job),
      isFeatureEnabled: () => this.isBq6Enabled,
      detailsTitle: `${CONSTANTS.BUILD_QUALITY_STEP_TITLES.BQ6} Complete`,
      hasInternalReport: true,
      // BQ6 captures no signatures, so there is no homeowner email to resend.
      canResendHomeownerEmail: false,
      getBlockedReason: () => null
    },
    "BQ7": {
      stepName: CONSTANTS.BUILD_QUALITY_STEP_TITLES.BQ7,
      getStartText: job => (job.startedAt ? "Continue BQ7" : "Start BQ7"),
      getCompleteText: job => `BQ7 Score: ${job.score !== null && job.score !== undefined ? job.score.toFixed(2) : "N/A"}`,
      completeActionType: ScoreCardCompleteActionType.LINK,
      completeActionLabel: "View",
      canJobBeStarted: () => false,
      canJobBePerformedFromSummary: false,
      onStart: _job => { },
      onCompleteAction: job => this.openModal(job),
      isFeatureEnabled: () => true,
      detailsTitle: `${CONSTANTS.BUILD_QUALITY_STEP_TITLES.BQ7} Complete`,
      hasInternalReport: true,
      canResendHomeownerEmail: false,
      // CMs never perform BQ7, so its sheet only reports the scheduled inspection date.
      getBlockedReason: () => "Completed by your CCM \u2014 score appears here once done."
    },
    "BQ8": {
      stepName: CONSTANTS.BUILD_QUALITY_STEP_TITLES.BQ8,
      getStartText: job => {
        if (!this.isBq8ChecklistAvailable) {
          return "Sync Required to Start BQ8";
        }
        return job.startedAt ? "Continue BQ8" : "Start BQ8";
      },
      getCompleteText: _job => "BQ8 Complete",
      completeActionType: ScoreCardCompleteActionType.LINK,
      completeActionLabel: "View",
      canJobBeStarted: () => this.isBq8ChecklistAvailable,
      canJobBePerformedFromSummary: true,
      onStart: job => this.router.navigateWithPersistedState(["/cm/bq8/job", job.id], { state: { fromLotSummary: true } }),
      onCompleteAction: job => this.openModal(job),
      isFeatureEnabled: () => true,
      detailsTitle: `${CONSTANTS.BUILD_QUALITY_STEP_TITLES.BQ8} Complete`,
      hasInternalReport: true,
      canResendHomeownerEmail: true,

      getBlockedReason: () => (this.isBq8ChecklistAvailable
        ? null
        : "This checklist becomes available closer to the scheduled date.")
    },
    "BQ9": {
      stepName: CONSTANTS.BUILD_QUALITY_STEP_TITLES.BQ9,
      getStartText: job => {
        const isBQ8Completed = this.jobs.find(job => job.buildQualityStep === CONSTANTS.BUILD_QUALITY_STEPS.BQ8)?.isComplete || false;

        if (isBQ8Completed) {
          return job.startedAt ? "Continue BQ9" : "Start BQ9";
        }
        return "Pending BQ8 Completion";
      },
      getCompleteText: _job => "BQ9 Complete",
      completeActionType: ScoreCardCompleteActionType.LINK,
      completeActionLabel: "View",
      canJobBeStarted: () => {
        return this.jobs.find(job => job.buildQualityStep === CONSTANTS.BUILD_QUALITY_STEPS.BQ8)?.isComplete || false;
      },
      canJobBePerformedFromSummary: true,
      onStart: job => this.router.navigateWithPersistedState(["/cm/bq9/job", job.id, "celebration"], { state: { fromLotSummary: true } }),
      onCompleteAction: job => this.openModal(job),
      isFeatureEnabled: () => this.isBq9Enabled,
      detailsTitle: `${CONSTANTS.BUILD_QUALITY_STEP_TITLES.BQ9} Complete`,
      hasInternalReport: true,
      canResendHomeownerEmail: true,
      getBlockedReason: () => (this.definitions["BQ9"].canJobBeStarted()
        ? null
        : `This step unlocks once the ${CONSTANTS.BUILD_QUALITY_STEP_TITLES.BQ8} (BQ8) is complete.`)
    }
  };

  async ngOnInit(): Promise<void> {
    this.onlineSubscription = this.networkStatusService.online$.subscribe(
      online => (this.online = online)
    );
    this.isBq234Enabled = await firstValueFrom(this.featureService.isEnabled(FeatureFlag.EnableBQ234));
    this.isBq15Enabled = await firstValueFrom(this.featureService.isEnabled(FeatureFlag.EnableBQ15));
    this.isBq6Enabled = await firstValueFrom(this.featureService.isEnabled(FeatureFlag.EnableBQ6));
    this.isBq9Enabled = await firstValueFrom(this.featureService.isEnabled(FeatureFlag.EnableBQ9));

    const lotId = this.lot?.id
      ?? this.jobs.find(job => job.buildQualityStep === CONSTANTS.BUILD_QUALITY_STEPS.BQ8)?.lotId;
    this.isBq8ChecklistAvailable = lotId !== undefined
      ? await this.checklistService.hasChecklist(lotId)
      : false;
  }

  ngOnDestroy(): void {
    this.onlineSubscription?.unsubscribe();
  }

  getJobDefinition(job: InspectionJob): ScoreCardDefinition | undefined {
    return this.definitions[`BQ${job.buildQualityStep}`];
  }

  get activeCards(): ScoreCard[] {
    return this.cardsForStatus(ScoreCardStatus.Active);
  }

  get pendingCards(): ScoreCard[] {
    return this.cardsForStatus(ScoreCardStatus.Pending);
  }

  /** Completed steps read newest-first so the latest result sits closest to the work in progress. */
  get completeCards(): ScoreCard[] {
    return this.cardsForStatus(ScoreCardStatus.Complete).reverse();
  }

  get cardGroups(): ScoreCardGroup[] {
    return [
      { key: ScoreCardStatus.Active, label: "", items: this.activeCards },
      { key: ScoreCardStatus.Pending, label: "Pending", items: this.pendingCards },
      { key: ScoreCardStatus.Complete, label: "Complete", items: this.completeCards }
    ];
  }

  onCardClick(card: ScoreCard): void {
    if (card.status === ScoreCardStatus.Complete) {
      this.openCompletedStep(card);
      return;
    }

    if (card.status === ScoreCardStatus.Active) {
      card.definition.onStart(card.job);
      return;
    }

    this.stepSheetCard = card;
    this.isStepSheetOpen = true;
  }

  get stepSheetTitle(): string {
    const card = this.stepSheetCard;
    if (!card) {
      return "";
    }

    return card.definition.getBlockedReason() !== null
      ? this.getStepLabel(card)
      : "Start this step?";
  }

  get stepSheetMessage(): StepSheetMessage | null {
    const card = this.stepSheetCard;
    if (!card) {
      return null;
    }

    const scheduledDate = this.formatDate(card.job.scheduledDate);
    const blockedReason = card.definition.getBlockedReason();

    if (blockedReason !== null) {
      return {
        leading: "Scheduled for ",
        emphasis: scheduledDate,
        trailing: `. ${blockedReason}`
      };
    }

    return {
      leading: "",
      emphasis: this.getStepLabel(card),
      trailing: ` is scheduled for ${scheduledDate}. Starting it now adds it to your active steps.`
    };
  }

  private getStepLabel(card: ScoreCard): string {
    return `${card.job.buildQualityStep} - ${card.definition.stepName}`;
  }

  get stepSheetButtons(): VerticalButton[] {
    const card = this.stepSheetCard;
    if (!card) {
      return [];
    }

    if (card.definition.getBlockedReason() !== null) {
      return [{
        label: "Close",
        severity: "contrast",
        styleClass: "bqa-primary-btn",
        action: () => this.closeStepSheet(),
        testId: "cm-btn-step-sheet-close"
      }];
    }

    return [
      {
        label: "Start Now",
        severity: "contrast",
        styleClass: "bqa-primary-btn",
        action: () => void this.startStepEarly(card),
        testId: "cm-btn-step-sheet-start"
      },
      {
        label: "Not Now",
        severity: "contrast",
        outlined: true,
        styleClass: "bqa-primary-btn",
        action: () => this.closeStepSheet(),
        testId: "cm-btn-step-sheet-not-now"
      }
    ];
  }

  closeStepSheet(): void {
    this.isStepSheetOpen = false;
    this.stepSheetCard = null;
  }

  async startStepEarly(card: ScoreCard): Promise<void> {
    await this.jobDataService.startJob(card.job);
    this.closeStepSheet();
  }

  private cardsForStatus(status: ScoreCardStatus): ScoreCard[] {
    return this.jobs
      .map(job => this.createScoreCard(job))
      .filter((card): card is ScoreCard => card !== null && card.status === status)
      .sort((left, right) => {
        const scheduledDiff = this.dateUtils.normalizeToLocalDate(left.job.scheduledDate).getTime()
          - this.dateUtils.normalizeToLocalDate(right.job.scheduledDate).getTime();
        return scheduledDiff !== 0
          ? scheduledDiff
          : left.job.buildQualityStep - right.job.buildQualityStep;
      });
  }

  private createScoreCard(job: InspectionJob): ScoreCard | null {
    const definition = this.getJobDefinition(job);
    if (!definition || !definition.isFeatureEnabled()) {
      return null;
    }

    const status = this.getStatus(job, definition);

    return {
      job,
      definition,
      status,
      title: `${job.buildQualityStep}${STEP_NUMBER_SEPARATOR}${definition.stepName}`,
      subtext: this.getSubtext(job, status)
    };
  }

  private getStatus(job: InspectionJob, definition: ScoreCardDefinition): ScoreCardStatus {
    if (job.isComplete) {
      return ScoreCardStatus.Complete;
    }

    if (!definition.canJobBePerformedFromSummary || !definition.canJobBeStarted()) {
      return ScoreCardStatus.Pending;
    }

    if (job.startedAt) {
      return ScoreCardStatus.Active;
    }

    return this.isWithinReadyWindow(job.scheduledDate)
      ? ScoreCardStatus.Active
      : ScoreCardStatus.Pending;
  }

  private getSubtext(job: InspectionJob, status: ScoreCardStatus): string {
    if (status === ScoreCardStatus.Complete) {
      if (job.buildQualityStep === CONSTANTS.BUILD_QUALITY_STEPS.BQ7) {
        return `Score: ${job.score !== null && job.score !== undefined ? job.score.toFixed(2) : "N/A"}`;
      }
      return job.submittedAt ? `Completed ${this.formatDate(job.submittedAt)}` : "Completed";
    }

    if (status === ScoreCardStatus.Active) {
      return job.startedAt ? "In Progress" : "Start Now";
    }

    return `Scheduled: ${this.formatDate(job.scheduledDate)}`;
  }

  private isWithinReadyWindow(scheduledDate: Date): boolean {
    return this.dateUtils.businessDaysDiff(new Date(), scheduledDate) <= READY_WINDOW_BUSINESS_DAYS;
  }

  private formatDate(value: Date): string {
    return this.dateUtils.normalizeToLocalDate(value)
      .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  /**
   * Steps that capture no homeowner signature only ever expose the report, so tapping the card
   * opens that report directly rather than a modal holding a single option.
   */
  private opensReportDirectly(definition: ScoreCardDefinition): boolean {
    return definition.hasInternalReport && !definition.canResendHomeownerEmail;
  }

  private openCompletedStep(card: ScoreCard): void {
    const reportUrl = this.getInternalReportUrl(card.job);

    if (reportUrl && this.opensReportDirectly(card.definition)) {
      window.open(reportUrl, "_blank", "noopener");
      return;
    }

    card.definition.onCompleteAction(card.job);
  }

  // BQ2, BQ3 and BQ4 are single-area inspections that share one feature flag and one card layout; BQ1/BQ5 share another.
  private createSingleAreaInspectionDefinition(
    buildQualityStep: number,
    inspectionName: string,
    isFeatureEnabled: () => boolean,
    canResendHomeownerEmail: boolean = false
  ): ScoreCardDefinition {
    const stepLabel = `BQ${buildQualityStep}`;

    return {
      stepName: inspectionName,
      getStartText: job => (job.startedAt ? `Continue ${stepLabel}` : `Start ${stepLabel}`),
      getCompleteText: _job => `${stepLabel} Complete`,
      completeActionType: ScoreCardCompleteActionType.LINK,
      completeActionLabel: "View",
      canJobBeStarted: () => true,
      canJobBePerformedFromSummary: true,
      onStart: job => this.router.navigateWithPersistedState([`/cm/bq${buildQualityStep}/job`, job.id], { state: { fromLotSummary: true } }),
      onCompleteAction: job => this.openModal(job),
      isFeatureEnabled,
      detailsTitle: `${inspectionName} Complete`,
      hasInternalReport: true,
      // Only the steps that capture homeowner signatures (BQ1/BQ5) have an email to resend.
      canResendHomeownerEmail,
      getBlockedReason: () => null
    };
  }

  async openModal(job: InspectionJob): Promise<void> {
    this.activeJob = job;
    this.activeJobDefinition = this.getJobDefinition(job) ?? null;
    this.activeInternalReportUrl = this.getInternalReportUrl(job);
    this.activeJobIssueCount = await db.jobIssue.where("jobId").equals(job.id)
      .count();
    this.preClosingModalOpen = true;
  }

  getInternalReportUrl(job: InspectionJob): string | null {
    const externalLotId = this.lot?.externalLotId;
    if (!this.getJobDefinition(job)?.hasInternalReport || !externalLotId) {
      return null;
    }
    return `/reports/${externalLotId}?reportStep=BQ${job.buildQualityStep}`;
  }

  get detailsActions(): HorizontalButton[] {
    const buttons: HorizontalButton[] = [];

    if (this.activeJobDefinition?.canResendHomeownerEmail) {
      buttons.push({
        label: this.sendingEmails ? "Sending..." : "Resend Email",
        icon: this.sendingEmails ? "pi pi-sync spin-cw" : "pi pi-send",
        iconPos: "top",
        disabled: this.sendingEmails,
        styleClass: "bqa-stacked-btn",
        action: () => this.resendEmailsToHomeowners(),
        testId: "cm-btn-resend-homeowner"
      });
    }

    if (this.activeInternalReportUrl) {
      buttons.push({
        label: "View Report",
        icon: "pi pi-external-link",
        iconPos: "top",
        styleClass: "bqa-stacked-btn",
        action: () => this.viewInternalReport(),
        testId: `cm-btn-view-report-bq${this.activeJob?.buildQualityStep}`
      });
    }

    return buttons;
  }

  viewInternalReport(): void {
    if (!this.activeInternalReportUrl) {
      return;
    }

    window.open(this.activeInternalReportUrl, "_blank", "noopener");
  }

  async resendEmailsToHomeowners(): Promise<void> {
    if (!this.online) {
      this.toastService.error("Cannot send emails while offline. Try again later.");
      return;
    }
    this.sendingEmails = true;
    this.homeownerService.ResendEmailToHomeowners(this.activeJob!.id).subscribe({
      complete: () => {
        this.toastService.success("Email(s) resent successfully.");
        this.sendingEmails = false;
        this.preClosingModalOpen = false;
      },
      error: () => {
        this.toastService.error("An error occurred while trying to send the email(s). Try again later.");
        this.sendingEmails = false;
      }
    });
  }
}
